/**
 * ScenarioProjectionsBase - Unified Scenario Projections Component
 * 
 * Consolidates ScenarioProjections.tsx and ScenarioProjectionsAccountant.tsx
 * into a single reusable component that accepts a token getter function.
 * 
 * Usage:
 * - For regular users (Clerk auth): Pass getToken from useAuth()
 * - For accountants: Pass token directly from AccountantAuthContext
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/lib/http';
import { Sliders, TrendingUp, TrendingDown, BarChart3, Save, Trash2, AlertCircle, HelpCircle, Shield, ChevronDown } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Info tooltip component for explanations
const InfoTooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-4 h-4 cursor-help inline" style={{ color: 'var(--color-text-dim)' }} />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 text-xs rounded-md -left-28 top-6 border" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {text}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 border-l border-t rotate-45" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}></div>
    </div>
  </div>
);

interface ScenarioProjectionsBaseProps {
  reportId: string;
  baseResult: any;
  // Token can be provided either as a getter function (Clerk) or direct string (Accountant)
  getToken?: () => Promise<string | null>;
  token?: string | null;
}

export default function ScenarioProjectionsBase({ 
  reportId, 
  baseResult, 
  getToken,
  token: directToken
}: ScenarioProjectionsBaseProps) {
  // Base values from report
  const baseIndemnity = baseResult?.indemnity_period_days || 90;
  const baseClaim = baseResult?.final_claim_amount || 0;
  const baseDailyGP = baseResult?.daily_gross_profit || 0;
  
  // Scenario adjustments
  const [indemnityDays, setIndemnityDays] = useState(baseIndemnity);
  const [revenueDropPct, setRevenueDropPct] = useState(0);
  const [expenseReductionPct, setExpenseReductionPct] = useState(0);
  
  // Results
  const [scenarioResult, setScenarioResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Mitigation analysis state
  const [mitigationExpanded, setMitigationExpanded] = useState(false);
  const [marketingMaintained, setMarketingMaintained] = useState(false);
  const [tempRelocation, setTempRelocation] = useState(false);
  const [altRevenue, setAltRevenue] = useState(false);
  const [expenseReduction, setExpenseReduction] = useState(false);
  const [resumeTimeline, setResumeTimeline] = useState<string>('significantly_delayed');
  const [mitigationResult, setMitigationResult] = useState<any>(null);
  const [mitigationLoading, setMitigationLoading] = useState(false);
  const [mitigationError, setMitigationError] = useState<string | null>(null);

  // Get auth token (supports both Clerk and Accountant auth)
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (directToken) {
      return directToken;
    }
    if (getToken) {
      return await getToken();
    }
    return null;
  }, [directToken, getToken]);

  // Calculate scenario function
  const calculateScenario = useCallback(async () => {
    if (!reportId) {
      console.warn('❌ No report ID provided');
      return;
    }
    
    // Wait for token if using accountant auth
    if (!directToken && !getToken) {
      console.log('⏳ No auth method provided');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const token = await getAuthToken();
      
      if (!token) {
        console.log('⏳ Waiting for authentication token...');
        return;
      }
      
      console.log('📊 Calculating scenario with:', {
        reportId,
        indemnityDays,
        revenueDropPct,
        expenseReductionPct,
        backendUrl: BACKEND_URL
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/scenarios/calculate/${reportId}`,
        {
          indemnity_period_days: indemnityDays,
          revenue_drop_pct: revenueDropPct,
          expense_reduction_pct: expenseReductionPct
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('✅ Scenario result:', response.data);
      
      // Extract scenario data from the response
      const scenarioData = response.data.scenario || response.data;
      const comparison = scenarioData.comparison || {};
      const scenarioResults = scenarioData.scenario_results || {};
      
      // Build a normalized result object for the UI
      const normalizedResult = {
        projected_claim: comparison.scenario_claim_amount || scenarioResults.final_claim_amount || 0,
        daily_gross_profit: comparison.scenario_daily_gp || scenarioResults.daily_gross_profit || 0,
        revenue_adjustment: scenarioResults.business_revenue ? 
          (scenarioResults.business_revenue - (response.data.scenario?.scenario_results?.business_revenue || 0)) : 0,
        expense_savings: scenarioResults.adjustments_applied?.expense_reduction_pct ? 
          (scenarioResults.variable_expenses * (scenarioResults.adjustments_applied.expense_reduction_pct / 100)) : 0,
        method: scenarioResults.configuration?.config_source || 'Linear',
        sensitivity_analysis: scenarioData.sensitivity_analysis,
        but_for_projection: scenarioData.but_for_projection,
        comparison: comparison
      };
      
      setScenarioResult(normalizedResult);
    } catch (err: any) {
      console.error('❌ Error calculating scenario:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to calculate scenario');
    } finally {
      setLoading(false);
    }
  }, [reportId, indemnityDays, revenueDropPct, expenseReductionPct, getAuthToken, directToken, getToken]);

  // Calculate mitigation credit
  const calculateMitigation = useCallback(async () => {
    if (!reportId) return;
    if (!directToken && !getToken) return;

    try {
      setMitigationLoading(true);
      setMitigationError(null);
      const token = await getAuthToken();
      if (!token) return;

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/mitigation/calculate/${reportId}`,
        {
          mitigation_factors: {
            marketing_maintained: marketingMaintained,
            temp_relocation: tempRelocation,
            alt_revenue: altRevenue,
            expense_reduction: expenseReduction,
            resume_timeline: resumeTimeline,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMitigationResult(response.data.mitigation_credit);
    } catch (err: any) {
      console.error('Mitigation calculation error:', err);
      setMitigationError(
        err.response?.data?.detail || err.message || 'Failed to calculate mitigation credit'
      );
    } finally {
      setMitigationLoading(false);
    }
  }, [reportId, marketingMaintained, tempRelocation, altRevenue, expenseReduction, resumeTimeline, getAuthToken, directToken, getToken]);

  // Load existing mitigation credit on mount
  useEffect(() => {
    if (!reportId) return;
    if (!directToken && !getToken) return;

    const loadExisting = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;

        const response = await axios.get(
          `${BACKEND_URL}/api/v1/mitigation/${reportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.mitigation_credit) {
          setMitigationResult(response.data.mitigation_credit);
          // Restore factor state from saved inputs
          const inputs = response.data.mitigation_credit.inputs?.mitigation_factors;
          if (inputs) {
            setMarketingMaintained(inputs.marketing_maintained ?? false);
            setTempRelocation(inputs.temp_relocation ?? false);
            setAltRevenue(inputs.alt_revenue ?? false);
            setExpenseReduction(inputs.expense_reduction ?? false);
            setResumeTimeline(inputs.resume_timeline ?? 'significantly_delayed');
          }
        }
      } catch {
        // No saved mitigation - that's fine
      }
    };

    loadExisting();
  }, [reportId, directToken, getToken, getAuthToken]);

  // Auto-calculate mitigation when factors change (debounced)
  useEffect(() => {
    if (!mitigationExpanded) return;
    if (!directToken && !getToken) return;

    const timer = setTimeout(() => {
      calculateMitigation();
    }, 600);

    return () => clearTimeout(timer);
  }, [marketingMaintained, tempRelocation, altRevenue, expenseReduction, resumeTimeline, mitigationExpanded, calculateMitigation, directToken, getToken]);

  // Auto-calculate when parameters change
  useEffect(() => {
    if (directToken === null && !getToken) {
      return; // Wait for token
    }

    const timer = setTimeout(() => {
      if (expanded) {
        calculateScenario();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [indemnityDays, revenueDropPct, expenseReductionPct, expanded, calculateScenario, directToken, getToken]);

  // Calculate claim change
  const claimChange = scenarioResult?.projected_claim 
    ? scenarioResult.projected_claim - baseClaim
    : 0;
  const changePercent = baseClaim > 0 
    ? ((claimChange / baseClaim) * 100).toFixed(1)
    : '0';

  return (
    <div className="rounded-md border overflow-hidden mt-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer transition-all"
        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Sliders className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
            <div>
              <h3 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Loss Projection Scenarios</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>What-If Analysis for Settlement Negotiations</p>
            </div>
          </div>
          <svg
            className={`w-6 h-6 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            style={{ color: 'var(--color-text-secondary)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="border rounded-md p-4 flex items-start space-x-3" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Calculation Error</p>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
              </div>
            </div>
          )}

          {/* Parameter Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Indemnity Period */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <TrendingUp className="w-4 h-4 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
                Indemnity Period (Days)
                <InfoTooltip text="The number of days the business was interrupted. Longer periods typically result in higher claims." />
              </label>
              <input
                type="range"
                min="30"
                max="365"
                value={indemnityDays}
                onChange={(e) => setIndemnityDays(parseInt(e.target.value))}
                className="w-full h-2 rounded-md appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', accentColor: 'var(--color-brand)' }}
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>30 days</span>
                <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>{indemnityDays} days</span>
                <span>365 days</span>
              </div>
            </div>

            {/* Revenue Drop */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <TrendingDown className="w-4 h-4 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
                Additional Revenue Drop (%)
                <InfoTooltip text="Extra revenue decline beyond what's already captured. Use for conservative scenarios." />
              </label>
              <input
                type="range"
                min="-50"
                max="50"
                value={revenueDropPct}
                onChange={(e) => setRevenueDropPct(parseInt(e.target.value))}
                className="w-full h-2 rounded-md appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', accentColor: 'var(--color-brand)' }}
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>-50%</span>
                <span className="font-semibold" style={{ color: revenueDropPct < 0 ? 'var(--color-verified)' : revenueDropPct > 0 ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}>
                  {revenueDropPct > 0 ? '+' : ''}{revenueDropPct}%
                </span>
                <span>+50%</span>
              </div>
            </div>

            {/* Expense Reduction */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <BarChart3 className="w-4 h-4 mr-2" style={{ color: 'var(--color-verified)' }} />
                Variable Expense Reduction (%)
                <InfoTooltip text="Estimated expense savings during the interruption period (e.g., reduced labor, supplies)." />
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={expenseReductionPct}
                onChange={(e) => setExpenseReductionPct(parseInt(e.target.value))}
                className="w-full h-2 rounded-md appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', accentColor: 'var(--color-verified)' }}
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>0%</span>
                <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>{expenseReductionPct}%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Results Display */}
          <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Base Claim */}
              <div className="rounded-md p-4 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Base Claim</p>
                <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={baseResult?.final_claim_amount} />
                </p>
              </div>

              {/* Projected Claim */}
              <div className="rounded-md p-4 text-center border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Projected Claim</p>
                {loading ? (
                  <div className="flex justify-center items-center h-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
                  </div>
                ) : (
                  <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                    <FinancialValue value={scenarioResult?.projected_claim} />
                  </p>
                )}
              </div>

              {/* Change */}
              <div className="rounded-md p-4 text-center" style={{ backgroundColor: claimChange >= 0 ? 'transparent' : 'var(--color-bg-elevated)' }}>
                <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Change</p>
                <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: claimChange >= 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                  {claimChange >= 0 ? '+' : ''}<FinancialValue value={claimChange} />
                  <span className="text-sm ml-1">({changePercent}%)</span>
                </p>
              </div>
            </div>
          </div>

          {/* Scenario Details */}
          {scenarioResult && (
            <div className="mt-4 rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <h4 className="cs-h3 mb-3" style={{ color: 'var(--color-text-primary)' }}>Scenario Breakdown</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Daily Gross Profit:</span>
                  <span className="ml-2 font-semibold font-mono tabular-nums">
                    <FinancialValue value={scenarioResult.daily_gross_profit || baseDailyGP} />
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Revenue Adjustment:</span>
                  <span className="ml-2 font-semibold font-mono tabular-nums">
                    <FinancialValue value={scenarioResult.revenue_adjustment || 0} />
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Expense Savings:</span>
                  <span className="ml-2 font-semibold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                    <FinancialValue value={(scenarioResult.expense_savings || 0) > 0 ? -(scenarioResult.expense_savings || 0) : 0} />
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Projection Method:</span>
                  <span className="ml-2 font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {scenarioResult.method || 'Linear'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Presets */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Quick Presets:</span>
            <button
              onClick={() => {
                setIndemnityDays(baseIndemnity);
                setRevenueDropPct(0);
                setExpenseReductionPct(0);
              }}
              className="px-3 py-1 text-xs rounded-full transition"
              style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              Reset to Base
            </button>
            <button
              onClick={() => {
                setIndemnityDays(Math.round(baseIndemnity * 1.5));
                setRevenueDropPct(10);
                setExpenseReductionPct(5);
              }}
              className="px-3 py-1 text-xs rounded-full transition"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            >
              Conservative
            </button>
            <button
              onClick={() => {
                setIndemnityDays(Math.round(baseIndemnity * 2));
                setRevenueDropPct(20);
                setExpenseReductionPct(10);
              }}
              className="px-3 py-1 text-xs rounded-full transition"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            >
              Aggressive
            </button>
            <button
              onClick={() => {
                setIndemnityDays(365);
                setRevenueDropPct(-10);
                setExpenseReductionPct(20);
              }}
              className="px-3 py-1 text-xs rounded-full transition"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            >
              Maximum Coverage
            </button>
          </div>

          {/* ─── Mitigation Analysis Section ─── */}
          <div className="mt-6 border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
            <div
              className="flex items-center justify-between cursor-pointer group"
              onClick={() => setMitigationExpanded(!mitigationExpanded)}
            >
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <h4 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Mitigation Analysis</h4>
                <InfoTooltip text="The insured has a duty to mitigate losses. Failure to take reasonable steps can result in deductions from the claim. Toggle factors to model the mitigation credit." />
              </div>
              <div className="flex items-center space-x-3">
                {mitigationResult && !mitigationExpanded && (
                  <span className="text-sm font-medium px-2.5 py-0.5 rounded-full" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                    Credit: <FinancialValue value={mitigationResult.mitigation_credit_amount} /> ({mitigationResult.mitigation_credit_pct}%)
                  </span>
                )}
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-200 ${mitigationExpanded ? 'rotate-180' : ''}`}
                  style={{ color: 'var(--color-text-dim)' }}
                />
              </div>
            </div>

            {mitigationExpanded && (
              <div className="mt-4 space-y-4">
                {/* Mitigation Error */}
                {mitigationError && (
                  <div className="border rounded-md p-3 flex items-start space-x-2" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{mitigationError}</p>
                  </div>
                )}

                {/* Factor Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Marketing Maintained */}
                  <label className="flex items-center justify-between rounded-md p-3 cursor-pointer transition" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div className="flex-1">
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Marketing Maintained</span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>Continued marketing/advertising during loss period</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={marketingMaintained}
                        onClick={() => setMarketingMaintained(!marketingMaintained)}
                        className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors"
                        style={{ backgroundColor: marketingMaintained ? 'var(--color-brand)' : 'var(--color-border)' }}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full transition-transform ${marketingMaintained ? 'translate-x-4' : 'translate-x-0'}`} style={{ backgroundColor: 'var(--color-bg-surface)' }} />
                      </button>
                    </div>
                  </label>

                  {/* Temporary Relocation */}
                  <label className="flex items-center justify-between rounded-md p-3 cursor-pointer transition" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div className="flex-1">
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Temporary Relocation</span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>Established temporary operations to minimize disruption</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={tempRelocation}
                        onClick={() => setTempRelocation(!tempRelocation)}
                        className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors"
                        style={{ backgroundColor: tempRelocation ? 'var(--color-brand)' : 'var(--color-border)' }}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full transition-transform ${tempRelocation ? 'translate-x-4' : 'translate-x-0'}`} style={{ backgroundColor: 'var(--color-bg-surface)' }} />
                      </button>
                    </div>
                  </label>

                  {/* Alternative Revenue */}
                  <label className="flex items-center justify-between rounded-md p-3 cursor-pointer transition" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div className="flex-1">
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Alternative Revenue</span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>Pursued alternative revenue streams during loss period</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={altRevenue}
                        onClick={() => setAltRevenue(!altRevenue)}
                        className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors"
                        style={{ backgroundColor: altRevenue ? 'var(--color-brand)' : 'var(--color-border)' }}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full transition-transform ${altRevenue ? 'translate-x-4' : 'translate-x-0'}`} style={{ backgroundColor: 'var(--color-bg-surface)' }} />
                      </button>
                    </div>
                  </label>

                  {/* Expense Reduction */}
                  <label className="flex items-center justify-between rounded-md p-3 cursor-pointer transition" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div className="flex-1">
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Expense Reduction</span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>Reduced unnecessary expenses during loss period</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={expenseReduction}
                        onClick={() => setExpenseReduction(!expenseReduction)}
                        className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors"
                        style={{ backgroundColor: expenseReduction ? 'var(--color-brand)' : 'var(--color-border)' }}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full transition-transform ${expenseReduction ? 'translate-x-4' : 'translate-x-0'}`} style={{ backgroundColor: 'var(--color-bg-surface)' }} />
                      </button>
                    </div>
                  </label>
                </div>

                {/* Resume Timeline Dropdown */}
                <div className="rounded-md p-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Operations Resume Timeline</span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>How quickly operations resumed compared to industry standard</p>
                    </div>
                    <select
                      value={resumeTimeline}
                      onChange={(e) => setResumeTimeline(e.target.value)}
                      className="ml-4 text-sm border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2"
                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
                    >
                      <option value="on_time">On Time</option>
                      <option value="delayed">Delayed</option>
                      <option value="significantly_delayed">Significantly Delayed</option>
                    </select>
                  </div>
                </div>

                {/* Mitigation Results */}
                {mitigationLoading && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
                  </div>
                )}

                {mitigationResult && !mitigationLoading && (
                  <div className="space-y-3">
                    {/* Summary Card */}
                    <div className="border rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Total Mitigation Credit</p>
                          <p className="text-2xl font-bold mt-1 font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                            <FinancialValue value={mitigationResult.mitigation_credit_amount} />
                            <span className="text-sm font-normal ml-2">
                              ({mitigationResult.mitigation_credit_pct}% of claim)
                            </span>
                          </p>
                        </div>
                        {mitigationResult.cap_applied && (
                          <span className="text-xs font-medium px-2 py-1 rounded" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                            Cap Applied ({mitigationResult.max_credit_pct}% max)
                          </span>
                        )}
                      </div>
                      {mitigationResult.industry_adjustment && (
                        <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                          {mitigationResult.industry_adjustment}
                        </p>
                      )}
                    </div>

                    {/* Factor Breakdown Table */}
                    <div className="border rounded-md overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                            <th className="text-left px-4 py-2 font-medium" style={{ color: 'var(--color-text-secondary)' }}>Factor</th>
                            <th className="text-center px-4 py-2 font-medium" style={{ color: 'var(--color-text-secondary)' }}>Score</th>
                            <th className="text-right px-4 py-2 font-medium" style={{ color: 'var(--color-text-secondary)' }}>Deduction</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mitigationResult.factors?.map((factor: any, idx: number) => (
                            <tr key={idx} className="border-b last:border-0" style={{ borderColor: 'var(--color-border-faint)' }}>
                              <td className="px-4 py-2">
                                <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{factor.name}</span>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{factor.note}</p>
                              </td>
                              <td className="text-center px-4 py-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={
                                  factor.score === 100
                                    ? { backgroundColor: 'transparent', color: 'var(--color-verified)' }
                                    : factor.score === 50
                                    ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                                    : { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                                }>
                                  {factor.score}/100
                                </span>
                              </td>
                              <td className="text-right px-4 py-2 font-medium font-mono tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                                <FinancialValue value={factor.deduction > 0 ? -factor.deduction : 0} />
                                <span className="text-xs ml-1" style={{ color: 'var(--color-text-muted)' }}>({factor.deduction_pct}%)</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Methodology Note */}
                    <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
                      {mitigationResult.methodology}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
